import React, { FC, useMemo, useRef, useState, useEffect } from 'react';
import {
  VStack,
  Button,
  Flex,
  useDisclosure,
  Input,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useMergeRefs,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useGiftCardBalance } from '@domain/giftcard';
import { useCheckoutDispatch } from '@olo-web/client-state';
import { MotionCollapse } from '@olo-web/components/atoms/MotionCollapse';
import { AddButtonWithIcon } from '../../common';
import { useScreenSizeState } from '@olo-web/client-state';

const giftCardFormName = 'giftCard';

export const GiftCardInput: FC = () => {
  const { handleSubmit, formState, reset, register, watch } = useForm();
  const { isMdDown } = useScreenSizeState();
  const { isLoading, error, data } = useGiftCardBalance();
  const checkoutDispatch = useCheckoutDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  const watchGiftCard = watch(giftCardFormName, '');
  const [showError, setShowError] = useState(false);
  const giftCardHasNoBalance = useMemo(() => {
    if (!data) return null;
    return Number(data.remainingBalanceAmount) <= 0 ? 'This gift card has no balance' : null;
  }, [data]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const refs = useMergeRefs(inputRef, register());

  const handleAddGiftCard = () => {
    setShowError(true);
    inputRef?.current?.focus();
    if (!value?.length) {
      return null;
    }
    checkoutDispatch({
      type: 'SET_GIFTCARD',
      payload: value,
    });
  };

  const handleToggle = () => {
    if (isOpen) {
      onClose();
      setValue('');
      reset();
      checkoutDispatch({
        type: 'CLEAR_GIFTCARD',
      });
      return;
    }
    onOpen();
    setTimeout(() => {
      inputRef?.current?.focus();
      inputRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 250);
  };

  const errorMessage = useMemo(() => {
    if (!isOpen) return null;
    if (!watchGiftCard && formState?.isSubmitted) return 'Please add a gift card';
    else {
      const eMsg = giftCardHasNoBalance ? giftCardHasNoBalance : error?.message;
      if (!eMsg || eMsg === 'Internal Error') {
        return `Error adding gift card: Please check your gift card number`;
      }
      return eMsg;
    }
  }, [watchGiftCard, isOpen, error, formState?.isSubmitted, giftCardHasNoBalance]);

  useEffect(() => {
    if (watchGiftCard === '') reset();
    setShowError(false);
    // eslint-disable-next-line
  }, [watchGiftCard]);

  return (
    <VStack w="100%" bg="white" spacing={0} alignItems="stretch" data-testid="add-gift-card">
      <AddButtonWithIcon
        label="Add gift card"
        onClick={handleToggle}
        isOpen={isOpen}
        labelProps={{
          color: 'blackAlpha.500',
        }}
        height={6}
        aria-expanded={isOpen}
        aria-controls="collapse_giftcard"
      />
      <MotionCollapse isOpen={isOpen} id="collapse_giftcard">
        <FormControl
          mt={4}
          isInvalid={
            !!(error || giftCardHasNoBalance || (!watchGiftCard && formState?.isSubmitted))
          }
        >
          <FormLabel mb={1} fontSize="0.9rem" fontWeight="normal" color="blackAlpha.400">
            Gift card number
          </FormLabel>
          <form onSubmit={handleSubmit(handleAddGiftCard)}>
            <Flex w="100%" align={{ base: 'center' }}>
              <Input
                m={'1px'}
                maxW={!isMdDown && '230px'}
                onChange={handleChange}
                name={giftCardFormName}
                autoFocus={isOpen}
                aria-expanded={isOpen}
                inputMode="numeric"
                ref={refs}
                _placeholder={{ color: 'blackAlpha.350' }}
                borderColor="black"
                _hover={{ borderColor: 'black' }}
                data-testid="gift-card-number-input"
                _focus={{
                  borderColor: 'primary.400',
                }}
              />
              <Button
                variant="outline"
                w="82px"
                colorScheme="primary"
                isLoading={isLoading}
                ml={4}
                type="submit"
                data-testid="apply-gift-card-button"
                aria-label="Add gift card"
              >
                Add
              </Button>
            </Flex>
          </form>
          {showError && (
            <FormErrorMessage data-testid="gift-card-error" role="alert" color="danger.400">
              {errorMessage}
            </FormErrorMessage>
          )}
        </FormControl>
      </MotionCollapse>
    </VStack>
  );
};
